import type { Vec2 } from './vec2';
import { cross2, dot2, lerp2, sub2, distSq2 } from './vec2';
import { orientationSign } from './predicates';
import { GEOMETRY_EPSILON, PARALLEL_EPSILON } from '@/constants/numeric';

export interface SegmentIntersection {
  /** 'none' | 'point' | 'overlap' (collinear, non-zero overlap length). */
  kind: 'none' | 'point' | 'overlap';
  /** Parameter along segment 1 (0 at p1, 1 at p2). Present for 'point'. */
  t?: number;
  /** Parameter along segment 2. Present for 'point'. */
  u?: number;
  point?: Vec2;
  /** For 'overlap', the parameter interval on segment 1. */
  overlap?: [number, number];
}

/**
 * Robust segment-segment intersection.
 *
 * Uses exact orientation predicates for the "do they cross" decision, then
 * computes the crossing point with the standard parametric formula. Endpoint
 * touches and collinear overlaps are reported explicitly rather than silently
 * dropped, because the planariser needs both.
 */
export function segmentIntersection(
  p1: Vec2,
  p2: Vec2,
  q1: Vec2,
  q2: Vec2,
): SegmentIntersection {
  const d1 = sub2(p2, p1);
  const d2 = sub2(q2, q1);
  const denom = cross2(d1, d2);

  const o1 = orientationSign(p1, p2, q1);
  const o2 = orientationSign(p1, p2, q2);
  const o3 = orientationSign(q1, q2, p1);
  const o4 = orientationSign(q1, q2, p2);

  if (Math.abs(denom) > PARALLEL_EPSILON) {
    // Proper (or endpoint-touching) crossing iff orientations straddle.
    if (o1 * o2 > 0 || o3 * o4 > 0) return { kind: 'none' };
    const t = cross2(sub2(q1, p1), d2) / denom;
    const u = cross2(sub2(q1, p1), d1) / denom;
    if (t < -GEOMETRY_EPSILON || t > 1 + GEOMETRY_EPSILON) return { kind: 'none' };
    if (u < -GEOMETRY_EPSILON || u > 1 + GEOMETRY_EPSILON) return { kind: 'none' };
    return { kind: 'point', t, u, point: lerp2(p1, p2, t) };
  }

  // Parallel. Collinear only if q1 lies on line(p1,p2).
  if (o1 !== 0 || o2 !== 0) return { kind: 'none' };

  const lenSq = dot2(d1, d1);
  if (lenSq < GEOMETRY_EPSILON * GEOMETRY_EPSILON) {
    // Degenerate first segment: treat as point-on-segment test.
    return distSq2(p1, q1) < GEOMETRY_EPSILON * GEOMETRY_EPSILON
      ? { kind: 'point', t: 0, u: 0, point: p1 }
      : { kind: 'none' };
  }

  const ta = dot2(sub2(q1, p1), d1) / lenSq;
  const tb = dot2(sub2(q2, p1), d1) / lenSq;
  const lo = Math.min(ta, tb);
  const hi = Math.max(ta, tb);
  const s = Math.max(0, lo);
  const e = Math.min(1, hi);
  if (e < s - GEOMETRY_EPSILON) return { kind: 'none' };
  if (Math.abs(e - s) <= GEOMETRY_EPSILON) {
    return { kind: 'point', t: s, u: 0, point: lerp2(p1, p2, s) };
  }
  return { kind: 'overlap', overlap: [s, e] };
}

/** Closest point on segment ab to p, plus its parameter. */
export function closestPointOnSegment(
  p: Vec2,
  a: Vec2,
  b: Vec2,
): { point: Vec2; t: number; distance: number } {
  const ab = sub2(b, a);
  const lenSq = dot2(ab, ab);
  if (lenSq < GEOMETRY_EPSILON * GEOMETRY_EPSILON) {
    return { point: a, t: 0, distance: Math.sqrt(distSq2(p, a)) };
  }
  let t = dot2(sub2(p, a), ab) / lenSq;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const point = lerp2(a, b, t);
  return { point, t, distance: Math.sqrt(distSq2(p, point)) };
}

/** Intersection of two infinite lines given by point + direction. */
export function lineLineIntersection(
  p: Vec2,
  dp: Vec2,
  q: Vec2,
  dq: Vec2,
): Vec2 | null {
  const denom = cross2(dp, dq);
  if (Math.abs(denom) < PARALLEL_EPSILON) return null;
  const t = cross2(sub2(q, p), dq) / denom;
  return { x: p.x + dp.x * t, y: p.y + dp.y * t };
}
